import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Divider,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { ManagedUser } from 'src/api/users';
import { CustomerRole } from 'src/contexts/AuthContext';
import { UserStatusBadge } from '../UserStatusBadge';
import { dayjs } from 'src/libs/dayjs';

type EditUserDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  user: ManagedUser | null;
  onSave: (data: {
    email?: string;
    displayName?: string;
    role?: CustomerRole;
  }) => void;
  onResendInvite: () => void;
  isSaving: boolean;
  isResending: boolean;
  onCloseComplete?: () => void;
};

const formatDate = (isoString: string | null) => {
  if (!isoString) return '-';
  return dayjs(isoString).format('MMM DD, YYYY');
};

export const EditUserDrawer: React.FC<EditUserDrawerProps> = ({
  isOpen,
  onClose,
  user,
  onSave,
  onResendInvite,
  isSaving,
  isResending,
  onCloseComplete,
}) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<CustomerRole>(CustomerRole.USER);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setDisplayName(user.displayName || '');
      setRole(user.role);
      setHasChanges(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const emailChanged = email !== user.email;
      const nameChanged = displayName !== (user.displayName || '');
      const roleChanged = role !== user.role;
      setHasChanges(emailChanged || nameChanged || roleChanged);
    }
  }, [email, displayName, role, user]);

  const handleSave = useCallback(() => {
    onSave({
      email: email.trim() !== user?.email ? email.trim() : undefined,
      displayName: displayName.trim() || undefined,
      role,
    });
  }, [email, displayName, role, onSave, user?.email]);

  if (!user) return null;

  const isPending = user.status === 'pending';

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      onCloseComplete={onCloseComplete}>
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12}>
          <Box display="flex" flexDirection="column" height="100%">
            <HStack justify="space-between" align="center" mb={2}>
              <Text
                fontFamily="marketingPage"
                fontWeight="bold"
                fontSize="32px"
                color="content.title">
                Edit User Details
              </Text>
              <UserStatusBadge status={user.status} />
            </HStack>
          </Box>
        </DrawerHeader>

        <DrawerBody p={6}>
          <VStack align="stretch" spacing={6}>
            {/* User Info Section */}
            <Box>
              <Text
                fontFamily="marketingPage"
                fontWeight="bold"
                fontSize="md"
                color="content.title"
                mb={2}>
                User Information
              </Text>
              {user.joinedAt && (
                <Text fontSize="sm" color="content.muted">
                  Joined: {formatDate(user.joinedAt)}
                </Text>
              )}
              {user.invitedAt && (
                <Text fontSize="sm" color="content.muted">
                  Invited: {formatDate(user.invitedAt)}
                </Text>
              )}
            </Box>

            <Divider />

            {/* Edit Form */}
            <FormControl>
              <FormLabel
                fontFamily="marketingPage"
                fontWeight={500}
                fontSize="16px"
                color="content.title"
                mb={3}>
                Email
              </FormLabel>
              <Input
                type="email"
                placeholder="user@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                bg="surface.panel"
                border="1px solid"
                borderColor="surface.border"
                borderRadius="md"
                py={3}
                px={4}
                fontSize="16px"
                fontFamily="marketingPage"
                _placeholder={{ color: 'content.muted' }}
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel
                fontFamily="marketingPage"
                fontWeight={500}
                fontSize="16px"
                color="content.title"
                mb={3}>
                User Name
              </FormLabel>
              <Input
                placeholder="Enter user name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                bg="surface.panel"
                border="1px solid"
                borderColor="surface.border"
                borderRadius="md"
                py={3}
                px={4}
                fontSize="16px"
                fontFamily="marketingPage"
                _placeholder={{ color: 'content.muted' }}
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel
                fontFamily="marketingPage"
                fontWeight={500}
                fontSize="16px"
                color="content.title"
                mb={3}>
                Role
              </FormLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as CustomerRole)}
                bg="surface.panel"
                border="1px solid"
                borderColor="surface.border"
                borderRadius="md"
                fontSize="16px"
                fontFamily="marketingPage"
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Select>
            </FormControl>

            {/* Pending User Actions */}
            {isPending && (
              <>
                <Divider />
                <Box>
                  <Text
                    fontFamily="marketingPage"
                    fontWeight="bold"
                    fontSize="md"
                    color="content.title"
                    mb={2}>
                    Pending Invite Actions
                  </Text>
                  <Button
                    bg="surface.panel"
                    color="content.title"
                    border="1px solid"
                    borderColor="icon.green"
                    size="md"
                    px={4}
                    py={2}
                    borderRadius="md"
                    fontWeight="600"
                    buttonName="resend-invite"
                    fontFamily="marketingPage"
                    onClick={onResendInvite}
                    isLoading={isResending}
                    w="100%"
                    _hover={{ bg: 'green.50' }}>
                    Resend Invite
                  </Button>
                </Box>
              </>
            )}
          </VStack>
        </DrawerBody>

        <Box p={6} display="flex" gap={4}>
          <Button
            bg="surface.sidebarMuted"
            color="content.body"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-edit"
            fontFamily="marketingPage"
            onClick={onClose}
            flex={1}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="save-user-changes"
            fontFamily="marketingPage"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!hasChanges}
            flex={1}
            _hover={{ bg: 'green.600' }}>
            Save Changes
          </Button>
        </Box>
      </DrawerContainer>
    </Drawer>
  );
};
